import { JSX, useEffect, useRef, useState } from 'react';
import { DateTime } from 'luxon';

import * as ContextualServerStateSlice from 'dg-web-shared/lib/ContextualServerStateSlice';
import { generateState, useStore, useUpdate } from 'dg-web-shared/lib/Flux';
import { AccountSetup } from '../../account-setup/components/AccountSetup';
import * as MetaServerState from '../../account/meta/state/MetaServerState';
import { SlideInPortalId } from '../../account/root/components/PortalSlidein';
import * as AsyncRequest from '../../AsyncRequest';
import * as Text from '../../common/i18n/Text';
import * as SettingsState from '../../common/state/SettingsState';
import { MobileHeader } from '../../layout/components/header/HeaderMobile';
import * as LayoutState from '../../layout/state/LayoutState';
import { columnsVariant } from '../../layout/utils/columnsVariant';
import { ResponsiveProperties } from '../../layout/utils/ResponsiveProperties';
import { responsiveVariant } from '../../layout/utils/responsiveVariant';
import { RunningCheckinByPlate } from '../../transactions-list/checkin-by-plate/RunningCheckinByPlate';
import { CheckinByPlateDetailState } from '../../transactions-list/state/TransactionsListState';
import { isProd } from '../../utils/Env';
import * as ParkCreateTexts from '../i18n/ParkCreateTexts';
import { ParkOptionList } from './ParkOptionList';
import { PaymentCallback } from '../../common/components/PaymentCallback';
import { css } from '@emotion/css';
import { Colors } from 'dg-web-shared/ui/vars';
import { paper } from 'dg-web-shared/tb-ui/paper.ts';
import { Typo } from 'dg-web-shared/ui/typo.ts';
import { TICKET_SCAN_BAR_HEIGHT_PX } from '../ticket/ParkCreateTicketPlus';
import { AuthStatusType, useAuthState } from '../../app/state/AuthState';
import { CoreAppOutlet } from '../../app/components/CoreAppOutletContext';
import { QrCodeScanner } from './QrCodeScanner';
import { EmptyBackground, SpinnerModal } from '../../ui/spinner/Spinner.tsx';
import { ModalErrorBox } from '../../ui/modals/Confirmable.tsx';
import { Localized, useLanguage } from 'dg-web-shared/lib/Localization.tsx';
import { useTransactionList } from '../../transactions-list/TransactionsListContext.tsx';

export interface Texts {
    Title: Text.Translation;
    TitleUnauth: Text.Translation;
}

export interface ParkCreateState {
    layout: LayoutState.State;
    settings: SettingsState.State;
    meta: MetaServerState.State;
    runningCheckinByPlate: CheckinByPlateDetailState.State | null;
}

export enum ParkCreateMode {
    transaction = 'transaction',
    permit = 'permit',
    badge = 'badge',
}

export function ParkCreate() {
    const [transactionList] = useTransactionList();
    const [showRunningCheckin, setShowRunningCheckin] = useState(true);
    const runningCheckinExternalId =
        transactionList.data?.runningCheckinExternalId ?? null;
    const previousRunningCheckinExternalId = useRef(runningCheckinExternalId);
    useEffect(() => {
        if (
            runningCheckinExternalId != null &&
            previousRunningCheckinExternalId.current !==
                runningCheckinExternalId
        ) {
            previousRunningCheckinExternalId.current = runningCheckinExternalId;
            setShowRunningCheckin(true);
        }
    }, [runningCheckinExternalId, setShowRunningCheckin]);
    const displayedCheckinExternalId = showRunningCheckin
        ? runningCheckinExternalId
        : null;

    const { storeState } = useStore<ParkCreateState>(store => {
        return {
            layout: new LayoutState.StateSlice(store).state,
            settings: new SettingsState.StateSlice(store).state,
            meta: new MetaServerState.StateSlice(store).state,
            runningCheckinByPlate:
                displayedCheckinExternalId != null
                    ? StartedCheckinByPlateOnThisDeviceDetailState.get(store, {
                          contractExternalId: displayedCheckinExternalId,
                      })
                    : null,
        };
    });
    const { authStatus } = useAuthState();

    const isMobile = new ResponsiveProperties(storeState).mobile;
    const renderAccountSetup =
        authStatus === AuthStatusType.OK &&
        storeState.meta.data &&
        !storeState.meta.data.registrationComplete;

    const metaPending = storeState.meta.pending;
    const slicesPending =
        metaPending ||
        !!(
            storeState.runningCheckinByPlate &&
            storeState.runningCheckinByPlate.pending &&
            !storeState.runningCheckinByPlate.data
        );

    const runningCheckinByPlateData = storeState.runningCheckinByPlate?.data;
    const hasRunningCheckinByPlate = !!(
        runningCheckinExternalId &&
        storeState.runningCheckinByPlate &&
        runningCheckinByPlateData &&
        runningCheckinByPlateData.isRunning &&
        runningCheckinByPlateData.autoTerminationTime &&
        DateTime.fromISO(runningCheckinByPlateData.autoTerminationTime) >
            DateTime.local()
    );

    // no idea where this comes from :(
    const bottomOffsetPixels =
        authStatus === AuthStatusType.OK && isMobile ? 48 : 0;

    const bottomScanBarOffsetPixel =
        isMobile && !hasRunningCheckinByPlate ? TICKET_SCAN_BAR_HEIGHT_PX : 0;

    return (
        <ParkCreateContainer layout={storeState.layout}>
            <SpinnerModal
                visible={slicesPending}
                portal={SlideInPortalId.PARK_CREATE}
            />
            {slicesPending ? (
                <EmptyBackground
                    hasRunningTransaction={!!runningCheckinExternalId}
                />
            ) : storeState.meta.statusCode.cls.error ? (
                <MetaDataErrorModal confirmDisabled={metaPending} />
            ) : renderAccountSetup ? (
                <AccountSetup />
            ) : (
                <div>
                    <ParkCreateHeader />
                    <div
                        className={css({
                            position: 'absolute',
                            width: '100%',
                            top:
                                authStatus !== AuthStatusType.OK &&
                                responsiveVariant(storeState) === 'Mobile'
                                    ? '64px'
                                    : '48px',
                            bottom: `  ${
                                bottomOffsetPixels + bottomScanBarOffsetPixel
                            }px`,
                        })}
                    >
                        <div
                            className={css({
                                position: 'relative',
                                height: '100%',
                                overflowX: 'hidden',
                                overflowY: 'auto',
                                WebkitOverflowScrolling: 'touch',
                            })}
                        >
                            {hasRunningCheckinByPlate ? (
                                <RunningCheckinByPlate
                                    isPrimaryTransactionInParkCreate={true}
                                    transaction={runningCheckinByPlateData}
                                    portal={SlideInPortalId.PARK_CREATE}
                                    setShowRunningCheckin={
                                        setShowRunningCheckin
                                    }
                                />
                            ) : (
                                <ParkOptionList />
                            )}
                        </div>
                    </div>
                    {!hasRunningCheckinByPlate && (
                        <QrCodeScanner isMobile={isMobile} />
                    )}
                    <CoreAppOutlet
                        context={{
                            hasRunningCheckinByPlate,
                        }}
                    />
                </div>
            )}
            {
                /* it's a bit strange and arbitrary to mount the datatrans callback here, but
                    otherwise it will not render since the modal div has not rendered yet
                    */
                <PaymentCallback />
            }
        </ParkCreateContainer>
    );
}

const ParkCreateContainer = (p: {
    layout: LayoutState.State;
    children?: JSX.Element[];
}) => {
    return (
        <div
            className={css({
                position: 'relative',
                background: Colors.blue,
                height: '100%',
                overflowX: 'hidden',
                overflowY: 'auto',
                WebkitOverflowScrolling: 'touch',
                ...paper(columnsVariant(p) === 'OneColumn' ? 0 : 4),
                borderRadius: columnsVariant(p) === 'OneColumn' ? 0 : '2px',
            })}
        >
            {p.children}
            <ParkCreateSlideIns />
        </div>
    );
};

const ParkCreateSlideIns = () => {
    return <div id={SlideInPortalId.PARK_CREATE} />;
};

function ParkCreateHeader() {
    const { storeState } = useStore(store => ({
        settings: new SettingsState.StateSlice(store).state,
        layout: new LayoutState.StateSlice(store).state,
    }));
    const responsiveProperties = new ResponsiveProperties(storeState);

    if (responsiveProperties.mobile) {
        return <MobileHeader />;
    }

    if (!responsiveProperties.mobile) {
        return <DesktopHeader />;
    }
    return null;
}

function DesktopHeader() {
    const { language } = useLanguage();
    const texts = ParkCreateTexts.parkCreateTexts[language];

    return (
        <div
            className={css({
                background: isProd() ? Colors.darkblue : Colors.yellow,
                height: '48px',
                color: Colors.white,
                ...Typo.heading1,
                paddingTop: 14 - Typo.heading1TopCorrection,
                paddingLeft: '24px',
            })}
        >
            {texts.Title()}
        </div>
    );
}

function MetaDataErrorModal({ confirmDisabled }: { confirmDisabled: boolean }) {
    const update = useUpdate();

    return (
        <ModalErrorBox
            confirmCallback={() =>
                update(store => {
                    new MetaServerState.StateSlice(store).reset();
                    return 'reset-meta';
                })
            }
            confirmDisabled={confirmDisabled}
            titleCaption={
                <Localized
                    de="Technischer Fehler"
                    fr="Technischer Fehler"
                    it="Technischer Fehler"
                    en="Technischer Fehler"
                />
            }
        >
            <p>
                <Localized
                    de="Ein unerwarteter Fehler ist aufgetreten."
                    fr="Une erreur inattendue s'est produite."
                    it="Si è verificato un errore imprevisto."
                    en="An unexpected error has occurred."
                />
            </p>
            <p>
                <Localized
                    de="Bitte versuchen Sie es erneut. Wenn das Problem weiterhin besteht, wenden Sie sich bitte an unseren Helpdesk.'"
                    it="La preghiamo di riprovare. Se il problema persiste contatti il nostro helpdesk."
                    fr="Veuillez réessayer. Si le problème persiste, veuillez contacter notre helpdesk."
                    en="Please try again. If the problem persists, contact our helpdesk."
                />
            </p>
        </ModalErrorBox>
    );
}

export namespace StartedCheckinByPlateOnThisDeviceDetailState {
    export const { get, getState, refetchSameContext } =
        ContextualServerStateSlice.generateContextualState<
            CheckinByPlateDetailState.Context,
            CheckinByPlateDetailState.CheckinByPlate,
            AsyncRequest.RawRequest
        >({
            key: 'park-create runningcheckinByPlateState',
            requestGenerator: CheckinByPlateDetailState.requestGenerator,
            request: AsyncRequest.requestV2,
            parseBody: b => b,
            refreshIntervalSecs: 60,
        });
}

export namespace ParkCreateAddVehicleState {
    export interface State {
        addVehicle: boolean;
        addBadge: boolean;
        selectedVehicleId: number | null;
    }

    export const { get, reset, stateWrite } = generateState<State>(
        'license-plate-selection-vehicle',
        {
            addVehicle: false,
            addBadge: false,
            selectedVehicleId: null,
        },
    );
}
